/** @jsxImportSource @emotion/react */
"use client";

import { useModal } from "@ebay/nice-modal-react";
import { useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { usePlayer } from "@packages/media";
import type { TrustedCollection } from "@packages/sdk";
import {
  redirectToAuth,
  useBranch,
  useRedirects,
  useRequestCollection,
  useRequestMe,
  useSession,
} from "@packages/sdk";
import type { MouseEvent } from "react";

import { Button } from "../../../../_base/_core/Button/Button.emotion";
import {
  ShareIcon,
  SubscriptionIcon,
} from "../../../../_base/icons/Icon/GeneralIcons";
import { PlayIcon } from "../../../../_base/icons/Icon/MediaIcons";
import { ShareModal } from "../../../../_base/modals/ShareModal/ShareModal.emotion";
import { JoinChallengeButton } from "../../JoinChallengeButton/JoinChallengeButton.emotion";

export type ChallengeCTAButtonProps = {
  collection: Partial<TrustedCollection>;
  sectionId?: number;
};

export const ChallengeCTAButton = ({
  collection,
  sectionId,
}: ChallengeCTAButtonProps) => {
  const analytics = useAnalytics();
  const t = useTranslations();
  const { status } = useSession();
  const { query: user } = useRequestMe();
  const { query: challenge } = useRequestCollection({ id: collection.id });
  const player = usePlayer();
  const shareModal = useModal(ShareModal);
  const branch = useBranch();
  const { redirectToSubscribe } = useRedirects();
  const needsToSubscribeToListen =
    collection.paid && !Boolean(user?.data?.subscription);

  const hasStarted = new Date() > new Date(collection.available_at);

  const playCollection = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (status === "authenticated") {
      if (needsToSubscribeToListen) {
        analytics.track({
          event: collection?.has_video
            ? HallowAnalyticsEvent.TappedSubscribeToWatch
            : HallowAnalyticsEvent.TappedSubscribeToListen,
          properties: {
            collection_id: collection?.id ?? null,
            section_id: sectionId ?? null,
          },
        });
        return redirectToSubscribe();
      }
      analytics.track({
        event: HallowAnalyticsEvent.TappedPlaySession,
        properties: {
          collection_id: collection?.id ?? null,
          section_id: sectionId ?? null,
        },
      });

      const playType = collection.is_album ? "collection" : "collection-next";

      await player?.playNew({ type: playType, id: collection.id });
    } else {
      analytics.track({
        event: collection.paid
          ? collection.has_video
            ? HallowAnalyticsEvent.TappedSubscribeToWatch
            : HallowAnalyticsEvent.TappedSubscribeToListen
          : collection.has_video
            ? HallowAnalyticsEvent.TappedWatchForFree
            : HallowAnalyticsEvent.TappedListenForFree,
        properties: {
          collection_id: collection?.id ?? null,
          section_id: sectionId ?? null,
        },
      });
      redirectToAuth();
    }
  };

  if (status === "unauthenticated" || !challenge?.data?.has_joined) {
    return <JoinChallengeButton collection={collection} />;
  }

  if (challenge?.data?.has_joined && challenge?.data?.is_completed) {
    return (
      <Button
        variant="white"
        startIcon={<ShareIcon />}
        onClick={async () =>
          shareModal.show({
            color: collection.images.color_hex,
            imgSrc: collection.images.large,
            title: collection.title,
            link: await branch.collection({ id: collection.id }),
          })
        }
      >
        {t("dialog_chapter_options_share")}
      </Button>
    );
  }

  if (!hasStarted) {
    return (
      <Button
        variant="white"
        onPointerDown={() => {
          analytics.track({
            event: HallowAnalyticsEvent.TappedInviteFriends,
            properties: {},
          });
        }}
        onClick={async () =>
          shareModal.show({
            color: collection.images.color_hex,
            imgSrc: collection.images.large,
            title: collection.title,
            link: await branch.collection({ id: collection.id }),
          })
        }
      >
        {t("web_share_challenge_invite_button_text")}
      </Button>
    );
  }

  return (
    <Button
      onClickCapture={playCollection}
      variant="white"
      startIcon={
        needsToSubscribeToListen ? (
          <SubscriptionIcon.Filled />
        ) : (
          <PlayIcon.Small />
        )
      }
    >
      {t(
        user?.data?.subscription
          ? "chapter_details_play_next_session"
          : collection.paid
            ? collection.has_video
              ? "button_subscribe_to_watch"
              : "button_subscribe_to_listen"
            : collection.has_video
              ? "button_watch_for_free"
              : "button_listen_for_free",
      )}
    </Button>
  );
};
